import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  GetFecalScoringsResponse,
  UpsertFecalScoringResponse,
} from './fecal-scoring-service.model';
import { FecalScoring } from '../model/fecal-scoring.model';

@Injectable({
  providedIn: 'root',
})
export class FecalScoringService {
  protected baseUrl = `${this.env.API_BASE_URL}/service/pig-registration/secure`;

  serviceWorkerRegistration: ServiceWorkerRegistration;

  constructor(
    protected http: HttpClient,
    @Inject(EnvironmentInjector) private readonly env: { API_BASE_URL: string },
    private readonly localeService: LocaleService,
  ) {}

  getFecalScoringById(id: number): Observable<FecalScoring | HttpErrorResponse> {
    return this.http.get<FecalScoring>(`${this.baseUrl}/fecalscore/${id}`).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<FecalScoring>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  getFecalScorings(): Observable<FecalScoring[] | HttpErrorResponse> {
    return this.http.get<GetFecalScoringsResponse>(`${this.baseUrl}/fecalscore`).pipe(
      map((response) => response.fecalScores),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<FecalScoring[]>[]);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  upsertFecalScoring(
    fecalScoring: FecalScoring,
  ): Observable<UpsertFecalScoringResponse | HttpErrorResponse> {
    return this.http
      .post<UpsertFecalScoringResponse>(`${this.baseUrl}/fecalscore`, fecalScoring, {
        headers: {
          OfflineIdentifier: 'UpsertFecalScoring',
          OfflineResponse: '{"id": 0, "offline": true}',
        },
      })
      .pipe(
        map((response) => response),
        catchError(([error, response]: [HttpErrorResponse, UpsertFecalScoringResponse]) => {
          if (error.status === 304) {
            return of({
              isOffline: true,
              id: response.id,
            } as UpsertFecalScoringResponse);
          } else if (error.status === 409) {
            return throwError({
              message: this.localeService.translate(
                'create-fecalscoring.error-code-already-in-use',
              ),
            });
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  deleteFecalScoring(fecalScoringId: number) {
    return this.http.delete(`${this.baseUrl}/fecalscore/${fecalScoringId}`);
  }
}
